import {yupResolver} from '@hookform/resolvers/yup';
import {showNotification} from 'platform/components';
import {HStack, Icon} from 'platform/foundation';

import {ChangeEvent, ReactElement, useEffect, useRef, useState} from 'react';
import {Controller, SubmitHandler, useForm} from 'react-hook-form';

import {
  useGetCebiaFeatureDecoderConfigQuery,
  usePatchCebiaFeatureDecoderConfigMutation,
} from '@dms/api/core';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {handleApiError} from '@dms/shared';
import {CebiaEquipmentCredentials, TextField} from '@dms/teas';

import {useOnMount} from 'shared/web';

import {ButtonSave, ButtonWrapper, WrapperCard} from '../utils/styles';
import {cebiaEquipmentAuthValidations} from '../utils/validations';

export const CebiaEquipmentAuthentication = (): ReactElement => {
  const [showPassword, setViewPassword] = useState(false);
  const {data: cebiaFeatureDecoderConfig} = useGetCebiaFeatureDecoderConfigQuery();
  const [patchCebiaFeatureDecoderConfig] = usePatchCebiaFeatureDecoderConfigMutation();
  const form = useForm<CebiaEquipmentCredentials>({
    resolver: yupResolver(cebiaEquipmentAuthValidations()),
    mode: 'onTouched',
    reValidateMode: 'onBlur',
    shouldFocusError: false,
    defaultValues: {
      username: '',
      password: '',
    },
  });
  const {
    formState,
    reset,
    control,
    formState: {errors},
  } = form;

  const wrapperRef = useRef<HTMLDivElement | null>(null);

  useOnMount(() => {
    const {hash} = window.location;

    // eslint-disable-next-line security/detect-possible-timing-attacks -- Muted pre-existing violation
    if (hash === '#cebia-equipment-login') {
      wrapperRef?.current?.scrollIntoView();
      (
        wrapperRef?.current?.querySelector('input[name="username"]') as HTMLInputElement | null
      )?.focus();
    }
  });

  useEffect(
    function resetCredentialsForm() {
      const username = cebiaFeatureDecoderConfig?.credentials?.username ?? '';
      reset({
        username,
        password: '',
      });
    },
    [cebiaFeatureDecoderConfig?.credentials?.username, reset]
  );

  const onValid: SubmitHandler<CebiaEquipmentCredentials> = (credentials) => {
    patchCebiaFeatureDecoderConfig({
      cebiaFeatureDecoderConfigRequestBody: {credentials},
    })
      .unwrap()
      .then(() => {
        showNotification.success(i18n.t('general.notifications.changesSuccessfullySaved'));
      })
      .catch(handleApiError);
  };

  const onSubmit = () => {
    form.handleSubmit(onValid)();
  };

  return (
    <WrapperCard
      ref={wrapperRef}
      id="cebia-equipment-authentication"
      title={i18n.t('general.labels.accessDataAuth')}
    >
      <HStack spacing={4}>
        <Controller
          name="username"
          control={control}
          render={({field: {onChange, value, ...rest}}) => (
            <TextField
              {...rest}
              type="text"
              value={value ?? ''}
              label={i18n.t('entity.user.labels.userName')}
              error={Boolean(errors.username?.message)}
              helperText={errors.username?.message}
              onChange={(e) => onChange(removeSpace(e))}
              data-testid="cebia-equipment-auth-form-field-username"
            />
          )}
        />
        <Controller
          name="password"
          control={control}
          render={({field: {onChange, value, ...rest}}) => (
            <TextField
              {...rest}
              type={showPassword || !formState.isDirty ? 'text' : 'password'}
              label={i18n.t('general.labels.password')}
              placeholder="•••••"
              value={value ?? ''}
              error={Boolean(errors.password?.message)}
              helperText={errors.password?.message}
              endAdornment={{
                disableFocusToInput: true,
                Icon: showPassword ? (
                  <Icon value="action/visibility" />
                ) : (
                  <Icon value="action/visibility_off" />
                ),
                onClick: () => setViewPassword(!showPassword),
              }}
              onChange={(e) => onChange(removeSpace(e))}
              data-testid="cebia-equipment-auth-form-field-password"
            />
          )}
        />
      </HStack>
      <ButtonWrapper>
        <ButtonSave
          primary
          disabled={!formState.isValid}
          onClick={onSubmit}
          data-testid={testIds.settings.vehicleServicesCebia('saveButton')}
        >
          {i18n.t('general.actions.save')}
        </ButtonSave>
      </ButtonWrapper>
    </WrapperCard>
  );
};

const removeSpace = (e: ChangeEvent<HTMLInputElement>) => e.target.value.replace(/\s+/g, '');
